import * as H from "history";
import * as React from "react";
import { Transition } from "react-transition-group";
import { pure } from "recompose";

import { Breadcrumb, BreadcrumbsBar } from "../../../../components/molecules";
import { BookingLocationPresenter } from "../../../../presenters/booking-location";
import { ITherapist } from "../../../../services/api/therapists";
import { dateFromIso } from "../../../../utils/index";
import { convertOldQueryParams } from "../../../../utils/therapist/therapist-query-string";

interface IProps {
  bookingLocation: BookingLocationPresenter;
  category: string;
  featuredTreatment: string;
  clearAddress(): void;
  clearCategory(): void;
  clearDate(): void;
  clearTherapist(): void;
  date: string;
  isMobile: boolean;
  location: H.Location;
  therapist: ITherapist;
}

const duration = {
  enter: 300,
  exit: 0,
};

const filterBreadcrumbsStyle = {
  transition: `all ${duration.enter}ms`,
};

const filterBreadcrumbsStyles = {
  entered: { opacity: 1, height: "56px" },
  entering: { opacity: 0, height: 0 },
  exited: { opacity: 0, height: 0 },
  exiting: { opacity: 1, height: "56px" },
};

const FilterBreadcrumbs: React.FC<IProps> = (props) => {
  const isShown = Boolean(
    props.bookingLocation || props.category || props.date,
  );

  return (
    <Transition in={isShown} timeout={duration}>
      {(state) => (
        <BreadcrumbsBar
          isShown={isShown}
          // tslint:disable-next-line: no-unsafe-any
          style={{
            ...filterBreadcrumbsStyle,
            ...filterBreadcrumbsStyles[state],
          }}
        >
          {props.bookingLocation && (
            <Breadcrumb
              clearState={props.clearAddress}
              icon="location"
              label={props.bookingLocation.getLabel(props.isMobile)}
              state={typeof props.bookingLocation !== "undefined"}
              title={
                props.isMobile ? undefined : props.bookingLocation.getTitle()
              }
            />
          )}
          {(props.category || props.featuredTreatment) && (
            <Breadcrumb
              clearState={props.clearCategory}
              icon="treatments"
              state={props.category || props.featuredTreatment ? true : false}
              title={`${props.category || "Featured"}${
                props.featuredTreatment ? `: ${props.featuredTreatment}` : ""
              }`}
            />
          )}
          {props.bookingLocation && props.date && (
            <Breadcrumb
              clearState={props.clearDate}
              icon="date"
              link={
                props.category &&
                `/category/${props.category}/therapists${props.location.search}`
              }
              state={props.date ? true : false}
              title={dateTimeText(props.date, props.isMobile)}
            />
          )}
          {props.bookingLocation && props.therapist && (
            <Breadcrumb
              avatar={props.therapist.logoImage}
              clearState={props.clearTherapist}
              icon="avatar"
              link={
                props.date && dateFromIso(props.date).hour !== 0
                  ? `/book/${props.therapist.urn}${
                    props.category && `/${props.category.toLowerCase()}`
                  }/${convertOldQueryParams(window.location.search)}`
                  : undefined
              }
              state={props.therapist ? true : false}
              title={props.therapist.name}
            />
          )}
        </BreadcrumbsBar>
      )}
    </Transition>
  );
};

/**
 * Conditional text for date & time breadcrumb section
 */
function dateTimeText(date: string, isMobile: boolean): string {
  const format = isMobile ? "ccc LLL dd" : "cccc LLLL dd";
  const dateResult =
    dateFromIso(date).hour === 0 ? format : `${format} - h:mm a`;

  return dateFromIso(date).toFormat(dateResult);
}

const pureComponent = pure(FilterBreadcrumbs);

export { pureComponent as FilterBreadcrumbs };
